"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { motion, AnimatePresence } from "framer-motion"
import { Menu, X, Search, Bell, User, ShoppingCart } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"

const navigation = [
  { name: "Home", href: "/" },
  { name: "Products", href: "/products" },
  { name: "Categories", href: "/categories" },
  { name: "About", href: "/about" },
  { name: "Contact", href: "/contact" },
]

export default function MobileHeader() {
  const [isOpen, setIsOpen] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }

    checkMobile()
    handleScroll()

    window.addEventListener("resize", checkMobile)
    window.addEventListener("scroll", handleScroll)

    return () => {
      window.removeEventListener("resize", checkMobile)
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  if (!isMobile) return null

  return (
    <>
      {/* Main Header */}
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
            ? "bg-white/95 backdrop-blur-xl shadow-lg border-b border-slate-200/50"
            : "bg-white/90 backdrop-blur-sm"
          }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              aria-label="Home"
              href="/" className="flex items-center space-x-2">
              <motion.div
                className="bg-gradient-to-br from-slate-900 to-slate-700 text-white p-2 rounded-xl shadow-lg"
                whileTap={{ scale: 0.95 }}
              >
                <span className="font-bold text-lg">RMM</span>
              </motion.div>
              <div>
                <div className="font-bold text-lg text-slate-900">Real Metro Metal</div>
                <p className="text-xs text-slate-600">Wholesale Hardware</p>
              </div>
            </Link>

            {/* Right Actions */}
            <div className="flex items-center space-x-2">
              {/* <motion.button
                className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                onClick={() => setShowSearch(!showSearch)}
                whileTap={{ scale: 0.95 }}
              >
                <Search className="h-5 w-5 text-slate-600" />
              </motion.button> */}

              {/* <motion.button
                className="p-2 rounded-full hover:bg-slate-100 transition-colors relative"
                whileTap={{ scale: 0.95 }}
              >
                <Bell className="h-5 w-5 text-slate-600" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
              </motion.button> */}

              {/* <motion.button
                className="p-2 rounded-full hover:bg-slate-100 transition-colors relative"
                whileTap={{ scale: 0.95 }}
              >
                <ShoppingCart className="h-5 w-5 text-slate-600" />
                <Badge className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-blue-600">
                  3
                </Badge>
              </motion.button> */}

              <motion.button
                className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                onClick={() => setIsOpen(!isOpen)}
                whileTap={{ scale: 0.95 }}
                aria-label={isOpen ? "Close menu" : "Open menu"}
              >
                {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </motion.button>
            </div>
          </div>

          {/* Search Bar */}
          <AnimatePresence>
            {showSearch && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-3"
              >
                <Input placeholder="Search products..." className="w-full bg-slate-50 border-0 rounded-xl" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 w-80 bg-white z-50 shadow-2xl"
            >
              <div className="p-6">
                {/* Profile Section */}
                <div className="flex items-center space-x-3 mb-8 pb-6 border-b border-slate-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-slate-900 to-slate-700 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Welcome</h3>
                    <p className="text-sm text-slate-600">Hardware Solutions</p>
                  </div>
                </div>

                {/* Navigation */}
                <nav className="space-y-2">
                  {navigation.map((item, index) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <Link
                        href={item.href}
                        className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors group focus:underline focus:outline-none underline-offset-4 hover:underline"
                        onClick={() => setIsOpen(false)}
                        aria-label={item.name}
                      >
                        <span className="font-medium text-slate-700 group-hover:text-slate-900">{item.name}</span>
                        <motion.div
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          whileHover={{ x: 5 }}
                        >
                          <span className="text-slate-400">→</span>
                        </motion.div>
                      </Link>
                    </motion.div>
                  ))}
                </nav>

                {/* CTA Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.5 }}
                  className="mt-8"
                >
                  <Button
                    asChild
                    className="w-full bg-gradient-to-r from-slate-900 to-slate-700 hover:from-slate-800 hover:to-slate-600 rounded-xl py-3"
                    aria-label="Get Quote"
                  >
                    <Link href="/contact" onClick={() => setIsOpen(false)}>
                      Get Quote
                    </Link>
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Spacer for fixed header */}
      <div className="h-16" />
    </>
  )
}
